/* ORIO Cassetto digitale v2 — dog-first bento layout.
   Big dog hero ("Il cassetto di …") with swipe switcher, then tiles:
   Spese (light, Pro) · ORIO AI scan (dark) · Scadenze · Documenti gallery ·
   Peso · Diario · Passaporto PDF. Everything opens in bottom sheets. */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Search, X, Plus, AlertTriangle, BookOpen, Receipt } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hapticTap, hapticSuccess } from "@/lib/haptics";
import Paywall from "@/components/Paywall";
import Diary from "@/pages/Diary";

import { DOC_SECTIONS, daysTo } from "./drawer/constants";
import BottomSheet from "./drawer/BottomSheet";
import DogHero from "./drawer/DogHero";
import { ExpensesTile } from "./drawer/ExpensesTile";
import { ExpenseAddSheet, ExpenseListSheet } from "./drawer/ExpenseSheets";
import { AiScanTile, ScadenzeTile, WeightTile, DiaryTile, PassportTile, DocSectionsGallery } from "./drawer/Tiles";
import { DocumentsList, Reminders, UploadDialog, WeightTimeline } from "./drawer/parts";

const PAYWALLS = {
  scan: { feature: "la scansione AI dei documenti", benefits: [
    "Fotografa qualunque documento sanitario", "L'AI estrae vet, vaccino, lotto, microchip, scadenza",
    "Le fatture diventano spese in automatico", "Cassetto illimitato + PDF passaporto UE"] },
  expenses: { feature: "le spese del tuo cane", benefits: [
    "Spese illimitate per ogni cane", "Riepilogo settimanale, mensile e annuale",
    "Ripartizione per cibo, veterinario, farmaci…", "Fatture scansionate → spese automatiche"] },
};

function useDrawerData() {
  const [state, setState] = useState({ dogs: [], docs: [], weights: [], summary: null, reminders: [], ready: false });
  const load = useCallback(async () => {
    const [a, b, s, w, r] = await Promise.all([
      api.get("/dogs"), api.get("/documents"),
      api.get("/drawer/summary").catch(() => ({ data: null })),
      api.get("/diary").catch(() => ({ data: [] })),
      api.get("/reminders").catch(() => ({ data: [] })),
    ]);
    setState({
      dogs: a.data.filter((d) => !d.is_stub), docs: b.data, summary: s.data,
      weights: (w.data || []).filter((e) => e.kind === "peso" && e.value),
      reminders: r.data || [], ready: true,
    });
  }, []);
  useEffect(() => { load(); }, [load]);
  return [state, load];
}

function sectionCounts(docs) {
  const out = {};
  for (const s of DOC_SECTIONS) {
    const list = docs.filter((d) => s.cats.includes(d.category) || (s.id === "altro" && !DOC_SECTIONS.some((x) => x.cats.includes(d.category))));
    out[s.id] = { list, total: list.length, expiring: list.filter((d) => { const t = daysTo(d.expires_at); return t !== null && t <= 30; }).length };
  }
  return out;
}

export default function Drawer() {
  const { user } = useAuth();
  const isPro = !!(user?.is_pro || user?.role === "admin");
  const [{ dogs, docs, weights, summary, reminders, ready }, load] = useDrawerData();
  const [dogId, setDogId] = useState("");
  const [period, setPeriod] = useState("month");
  const [expenses, setExpenses] = useState(null);
  const [addExp, setAddExp] = useState({ open: false, cat: null });
  const [listOpen, setListOpen] = useState(false);
  const [section, setSection] = useState(null);
  const [sheet, setSheet] = useState(null); // reminders | diary | weight
  const [upload, setUpload] = useState(null); // { autoScan, category }
  const [paywall, setPaywall] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => { if (!dogId && dogs.length) setDogId(dogs[0].id); }, [dogs, dogId]);
  const dog = dogs.find((d) => d.id === dogId);

  const loadExpenses = useCallback(async () => {
    if (!dogId) return;
    try { setExpenses((await api.get("/expenses", { params: { dog_id: dogId, period } })).data); }
    catch { setExpenses(null); }
  }, [dogId, period]);
  useEffect(() => { loadExpenses(); }, [loadExpenses]);

  const dogDocs = useMemo(() => docs.filter((d) => d.dog_id === dogId), [docs, dogId]);
  const counts = useMemo(() => sectionCounts(dogDocs), [dogDocs]);
  const expiringCount = useMemo(() => dogDocs.filter((d) => { const t = daysTo(d.expires_at); return t !== null && t <= 30; }).length, [dogDocs]);
  const dogReminders = useMemo(() => reminders.filter((r) => !r.completed_at && (!r.dog_id || r.dog_id === dogId)), [reminders, dogId]);
  const next = useMemo(() => {
    const r = [...dogReminders].sort((a, b) => new Date(a.due_date) - new Date(b.due_date))[0];
    return r ? { ...r, days: daysTo(r.due_date) } : null;
  }, [dogReminders]);
  const dogWeights = useMemo(() => weights.filter((w) => w.dog_id === dogId)
    .sort((a, b) => new Date(a.happened_at) - new Date(b.happened_at)), [weights, dogId]);
  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return null;
    return dogDocs.filter((d) => [d.title, d.category, d.notes, ...Object.values(d.key_data || {})]
      .filter(Boolean).join(" ").toLowerCase().includes(q));
  }, [dogDocs, search]);
  const criticalOverdue = (summary?.reminders?.critical_overdue || []).filter((c) => !c.dog_id || c.dog_id === dogId);

  const startScan = () => { hapticTap(); if (!isPro) return setPaywall("scan"); setUpload({ autoScan: true, category: "vaccino" }); };
  const delDoc = async (id) => {
    if (!window.confirm("Eliminare documento?")) return;
    await api.delete(`/documents/${id}`); toast.success("Eliminato"); load();
  };
  const exportPDF = async () => {
    if (!dog) return;
    hapticTap();
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dogs/${dog.id}/passport.pdf`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("orio_token")}` }, credentials: "include" });
      if (!res.ok) throw new Error();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(await res.blob());
      a.download = `ORIO_passaporto_${dog.name.replace(/\s/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      hapticSuccess(); toast.success("PDF passaporto scaricato");
    } catch { toast.error("Errore download PDF"); }
  };

  const sec = DOC_SECTIONS.find((s) => s.id === section);

  if (ready && dogs.length === 0) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-10" data-testid="cassetto-page">
        <div className="rounded-[28px] p-10 text-center bg-white border border-[#F0EBE3]">
          <BookOpen className="h-10 w-10 mx-auto text-[#C7C0B3]" />
          <div className="mt-3 font-medium">Crea prima un passaporto cane</div>
          <div className="text-[12px] text-[#6B6B6B] mt-1">Per archiviare documenti, spese e promemoria.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 py-5 sm:py-8 pb-28 md:pb-10" data-testid="cassetto-page">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.24em] text-[#9A9A9A] inline-flex items-center gap-1.5 font-semibold">
          <ShieldCheck className="h-3 w-3" /> Cassetto digitale
        </div>
        <motion.button whileTap={{ scale: 0.95 }} onClick={() => setUpload({ autoScan: false, category: "vaccino" })} data-testid="add-doc-btn"
          className="px-3.5 py-1.5 rounded-full text-[12.5px] font-semibold text-white inline-flex items-center gap-1"
          style={{ background: "linear-gradient(135deg,#FF7A1A,#F4B83C)" }}>
          <Plus className="h-3.5 w-3.5" /> Carica
        </motion.button>
      </div>

      {dog && <DogHero dogs={dogs} dogId={dogId} onChange={setDogId} docsCount={dogDocs.length} expiringCount={expiringCount} />}

      {criticalOverdue.length > 0 && (
        <motion.button initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} onClick={() => setSheet("reminders")}
          data-testid="critical-overdue-alert"
          className="mt-4 w-full rounded-[22px] p-3.5 flex items-center gap-3 text-left border border-[#E94545]/30"
          style={{ background: "linear-gradient(135deg, rgba(233,69,69,0.10), rgba(255,122,26,0.05))" }}>
          <span className="h-10 w-10 rounded-xl flex items-center justify-center text-white shrink-0" style={{ background: "linear-gradient(135deg,#E94545,#FF7A1A)" }}>
            <AlertTriangle className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#E94545]">Scadenza critica</div>
            <div className="text-[13.5px] text-[#1A1A1A] font-medium truncate">{criticalOverdue.map((c) => c.title).join(" · ")}</div>
          </div>
        </motion.button>
      )}

      <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="col-span-2 lg:row-span-2">
          <ExpensesTile data={expenses} dogName={dog?.name || ""} period={period} onPeriod={setPeriod}
            onAdd={(cat) => setAddExp({ open: true, cat })} onOpenList={() => setListOpen(true)} onLocked={() => setPaywall("expenses")} />
        </div>
        <div className="lg:col-span-2"><AiScanTile isPro={isPro} onStart={startScan} /></div>
        <div className="lg:col-span-2"><ScadenzeTile next={next} activeCount={dogReminders.length} onOpen={() => setSheet("reminders")} /></div>
      </div>

      <div className="mt-7 flex items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#9A9A9A] font-semibold">Archivio</div>
          <h2 className="font-display text-2xl text-[#1A1A1A] leading-tight">Documenti di {dog?.name}</h2>
        </div>
      </div>
      <div className="mt-3 mb-3 relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9A9A9A]" />
        <input data-testid="doc-search" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca titolo, veterinario, lotto, microchip…"
          className="w-full pl-10 pr-9 py-3 rounded-2xl bg-white border border-[#F0EBE3] outline-none text-[13.5px] focus:border-[#FF7A1A]/50" />
        {search && (
          <button data-testid="doc-search-clear" onClick={() => setSearch("")} aria-label="Pulisci"
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-[#9A9A9A]"><X className="h-3.5 w-3.5" /></button>
        )}
      </div>
      {searchResults ? (
        <DocumentsList docs={searchResults} daysTo={daysTo} onDelete={delDoc} />
      ) : (
        <DocSectionsGallery counts={counts} onOpen={setSection} />
      )}

      <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <WeightTile entries={dogWeights} fallbackKg={dog?.weight_kg} onOpen={() => setSheet("weight")} />
        <DiaryTile onClick={() => setSheet("diary")} />
        <div className="col-span-2"><PassportTile onClick={exportPDF} /></div>
      </div>

      <ExpenseAddSheet open={addExp.open} dogId={dogId} dogName={dog?.name || ""} initialCategory={addExp.cat}
        onClose={() => setAddExp({ open: false, cat: null })}
        onSaved={() => { setAddExp({ open: false, cat: null }); loadExpenses(); }}
        onLocked={() => { setAddExp({ open: false, cat: null }); setPaywall("expenses"); loadExpenses(); }} />
      <ExpenseListSheet open={listOpen} dogId={dogId} dogName={dog?.name || ""} period={period}
        onClose={() => setListOpen(false)} onChanged={loadExpenses} />

      <BottomSheet open={!!sec} onClose={() => setSection(null)} kicker={dog?.name} title={sec?.title || ""} testid="doc-section-sheet">
        {sec && (
          <>
            <motion.button whileTap={{ scale: 0.97 }} onClick={() => setUpload({ autoScan: false, category: sec.cats[0] })}
              data-testid="doc-section-add"
              className="mb-3 w-full py-3 rounded-2xl text-[13.5px] font-semibold text-white inline-flex items-center justify-center gap-1.5"
              style={{ background: sec.color }}>
              <Plus className="h-4 w-4" /> Aggiungi a {sec.title}
            </motion.button>
            {sec.id === "fatture" && (
              <div className="mb-3 rounded-2xl p-3 bg-[#FFF7ED] border border-[#F4B83C]/40 text-[12px] text-[#7A5A1F] flex gap-2">
                <Receipt className="h-4 w-4 shrink-0 text-[#B37016]" /> Le fatture scansionate con ORIO AI diventano spese in automatico.
              </div>
            )}
            <DocumentsList docs={counts[sec.id]?.list || []} daysTo={daysTo} onDelete={delDoc} />
          </>
        )}
      </BottomSheet>

      <BottomSheet open={sheet === "reminders"} onClose={() => setSheet(null)} kicker={dog?.name} title="Scadenze & promemoria" testid="reminders-sheet">
        <Reminders dogId={dogId} dogs={dogs} onChange={load} />
      </BottomSheet>
      <BottomSheet open={sheet === "diary"} onClose={() => setSheet(null)} kicker={dog?.name} title="Diario" testid="diary-sheet">
        <Diary dogId={dogId} />
      </BottomSheet>
      <BottomSheet open={sheet === "weight"} onClose={() => { setSheet(null); load(); }} kicker={dog?.name} title="Peso" testid="weight-sheet">
        {dogWeights.length > 0
          ? <WeightTimeline entries={dogWeights} dog={dog} isPro={isPro} onOpenDiary={() => setSheet("diary")} />
          : <div className="text-[13px] text-[#9A9A9A] mb-3">Nessuna rilevazione. Aggiungi il peso dal diario.</div>}
        <button onClick={() => setSheet("diary")} data-testid="weight-open-diary"
          className="w-full py-3 rounded-2xl bg-white border border-[#F0EBE3] text-[13.5px] font-semibold">Registra peso nel diario</button>
      </BottomSheet>

      {upload && (
        <UploadDialog dogId={dogId} autoScan={upload.autoScan} defaultCategory={upload.category}
          onClose={() => setUpload(null)}
          onDone={() => { setUpload(null); load(); loadExpenses(); }} />
      )}
      <Paywall open={!!paywall} onClose={() => setPaywall(null)}
        feature={PAYWALLS[paywall || "scan"].feature} benefits={PAYWALLS[paywall || "scan"].benefits} />
    </div>
  );
}
